/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty, pickBy } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { FirmCompany as CreatedFirmCompany } from 'api';
import { allocationsAction, captableAction, companiesAction } from 'common/actions';
import { companySummaryUrl, firmSummaryUrl } from 'common/config/urls';
import {
  COMPANIES_TOOLTIP_TEXT_COMPANY_MEASUREMENT_DATE,
  COMPANY_TYPE,
  RELATED_ENTITY,
} from 'common/constants/companies';
import { useStore } from 'common/store';
import { FirmCompany } from 'common/types/company';
import { FormDialog } from 'components';
import { ConfirmationDelete, ConfirmationDialog } from 'components/Dialogs';
import { CompanyForm } from 'components/Forms';
import { CompanyFormDefaultFormState } from 'components/Forms/CompanyForm/CompanyForm.props';
import DialogContext from 'context/DialogContext';
import { CompaniesService, FirmService } from 'services';
import { CompanyHk } from 'services/hooks';
import { NewCompanyDialogProps } from './NewCompanyDialog.props';
import { useCreateFirmCompany } from '../../../../services/hooks/useCreateFirmCompany';

const { useUpdateCompanyById } = CompanyHk;
const ContentComponent = (itemName: string) => <ConfirmationDelete itemName={itemName} />;

const NewCompanyDialog = ({ open, onClose, editMode = false, isRelatedEntity = false }: NewCompanyDialogProps) => {
  const { data: createdCompany, createCompany } = useCreateFirmCompany();
  const firmService = new FirmService();
  const [{ companyInfo, firmId, firmInfo }, dispatch] = useStore();
  const defaultValues = editMode
    ? companyInfo
    : {
      name: '',
      legal_company_name: '',
      website: '',
      fiscal_year_end: '',
      captable_currency: 'USD',
      financials_currency: 'USD',
    };
  const navigate = useNavigate();
  const { firmSlugParam } = useParams() as { firmSlugParam: string };
  const { showDialog } = useContext(DialogContext);
  const [formState, setFormState] = useState({
    isValid: editMode,
    values: defaultValues,
    errors: {},
  });

  const isEmptyCompany = isEmpty(pickBy(formState.values));

  const updateCompany = useUpdateCompanyById();
  const [allowDeleteCompany, setAllowDeleteCompany] = useState(true);

  const deleteCompany = async () => {
    try {
      const companySvc = new CompaniesService();
      await companySvc.deleteCompany(companyInfo.id);
      onClose();
      navigate({ pathname: firmSummaryUrl(firmSlugParam) });
      const companies = (await firmService.getCompanyListByFirmId(companyInfo.firm_id)) as FirmCompany[];
      dispatch(companiesAction.setCompanyList(companies));
    } catch (error) {
      return error;
    }
  };

  const handleDeleteCompany = () => {
    showDialog({
      wrapper: ConfirmationDialog,
      content: () => ContentComponent(`Company: ${companyInfo.name}`),
      actions: [
        {
          label: 'Delete',
          variant: 'contained',
          type: 'danger',
          callback: deleteCompany,
        },
      ],
    });
  };

  const saveData = async () => {
    if (editMode) {
      const { firm_id, name, legal_company_name, website, fiscal_year_end, captable_currency, financials_currency }
        = formState.values;
      const companyFormValues = {
        firm: firm_id,
        name,
        legal_company_name,
        website,
        fiscal_year_end,
        captable_currency,
        financials_currency,
      };
      updateCompany(companyInfo.id, companyFormValues, firmSlugParam);
    } else {
      const { values: companyData } = formState;

      const createPayload = isRelatedEntity
        ? {
          ...companyData,
          type: COMPANY_TYPE.HOLDING,
          parent_company: companyInfo.id,
          firm: firmId,
        }
        : {
          ...companyData,
          type: COMPANY_TYPE.PORTFOLIO,
          firm: firmId,
        };

      const newCompany = await createCompany(createPayload);

      if (newCompany && !isEmpty(firmInfo)) {
        dispatch(captableAction.setCaptableInfo({}));
        dispatch(allocationsAction.setAllocationInfo({}));
        navigate(companySummaryUrl(firmInfo.slug, newCompany.slug as string));
        onClose();
      }
    }
  };

  const getTitle = useCallback(() => {
    if (isRelatedEntity) {
      return RELATED_ENTITY.TITLE;
    }
    if (editMode) {
      return companyInfo.name;
    }
    return 'Add Company';
  }, [isRelatedEntity, editMode, companyInfo]);

  useEffect(() => {
    if (!editMode || isEmpty(companyInfo) || companyInfo?.measurement_date_id) {
      setAllowDeleteCompany(false);
    }
  }, [companyInfo]);

  useEffect(() => {
    if (!isEmpty(createdCompany) && !isEmpty(firmInfo)) {
      dispatch(captableAction.setCaptableInfo({}));
      dispatch(allocationsAction.setAllocationInfo({}));
      navigate(companySummaryUrl(firmInfo.slug, createdCompany.slug as string));
    }
  }, [createdCompany, firmInfo]);

  const shouldCheckFeatureEnabler = useMemo(() => !allowDeleteCompany, [allowDeleteCompany]);

  return (
    <FormDialog
      open={open}
      title={getTitle()}
      editMode={editMode}
      subtitle={isRelatedEntity ? RELATED_ENTITY.SUBTITLE : ''}
      onSave={saveData}
      onClose={onClose}
      onDelete={handleDeleteCompany}
      allowDelete={allowDeleteCompany}
      isValid={formState.isValid}
      confirmCancel={!isEmptyCompany}
      deleteText="Delete Company"
      toolTipText={COMPANIES_TOOLTIP_TEXT_COMPANY_MEASUREMENT_DATE}
      shouldCheckFeatureEnabler={shouldCheckFeatureEnabler}>
      <CompanyForm
        editMode={editMode}
        initialValues={defaultValues}
        defaultFormState={formState as unknown as CompanyFormDefaultFormState}
        onFormChange={formCurrentState => setFormState(formCurrentState)}
        isRelatedEntity={isRelatedEntity}
      />
    </FormDialog>
  );
};

export default NewCompanyDialog;
